import { FC } from 'react';
import {
  PageContentLayout,
  PageHeaderLayout,
  SideMenu,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'utils/navigation';
import {
  adminDepositBalancesPerCompanyFiltersSchema,
  useAdminDepositBalancesPerCompany,
} from 'store/admin-client-deposits';
import { formatCurrency } from 'utils/format';
import {
  AdminCompaniesFilter,
  DateFilter,
  FilterButton,
  Filters,
  ParentCompaniesFilter,
} from 'components/filters';
import { AdminClientDepositsActions } from 'containers/admin';
import { useRouteMatch } from 'react-router-dom';
import { APP, ROUTES } from 'config';
import { TabsSwitcher } from '../atoms';

export const AdminDepositBalancesListPerCompany: FC = () => {
  const { t, i18n } = useTranslation();
  const { path } = useRouteMatch();

  const { searchParams: filters, navigate } = useSearchParams(
    adminDepositBalancesPerCompanyFiltersSchema,
  );

  const { data: deposits, isLoading } =
    useAdminDepositBalancesPerCompany(filters);

  return (
    <Stack gap={2}>
      <PageHeaderLayout
        breadcrumbs={[
          {
            label: t('deposits.deposit_balance.title'),
          },
        ]}
      />

      <PageContentLayout
        sidebar={
          <SideMenu
            tabs={[
              {
                key: 'claims',
                label: t('deposits.deposit_balance.title'),
                url: ROUTES.admin.deposits.balances.companies.list,
                isActive:
                  path === ROUTES.admin.deposits.balances.companies.list,
              },
            ]}
          />
        }
      >
        <TableWrapper>
          <TableToolbar>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              gap={1}
              flexWrap="wrap"
            >
              <TabsSwitcher />

              <Stack direction="row" alignItems="center" gap="10px">
                <Filters
                  value={filters}
                  initialValues={adminDepositBalancesPerCompanyFiltersSchema.parse(
                    {},
                  )}
                  onChange={(v) => navigate({ params: { ...v, page: 1 } })}
                  showLabel={false}
                >
                  {({ value, initialValues, setFilter }) => (
                    <>
                      <FilterButton
                        label={t('marketplaceOrders.client')}
                        value={value.client}
                        initialValue={initialValues.client}
                        onChange={(v) => setFilter('client', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <ParentCompaniesFilter
                            value={value}
                            onApplyFilter={applyFilter}
                            onClearFilter={clearFilter}
                            onCancel={close}
                            multiple={false}
                          />
                        )}
                      </FilterButton>

                      {filters.client ? (
                        <FilterButton
                          label={t('employees.company')}
                          value={value.companies}
                          initialValue={initialValues.companies}
                          onChange={(v) => setFilter('companies', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <AdminCompaniesFilter
                              clientId={filters.client}
                              value={{ value }}
                              onApplyFilter={({ value }) => applyFilter(value)}
                              onClearFilter={clearFilter}
                              onCancel={close}
                            />
                          )}
                        </FilterButton>
                      ) : null}

                      <FilterButton
                        label={t('common.date')}
                        value={value.date}
                        initialValue={initialValues.date}
                        onChange={(v) => setFilter('date', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <DateFilter
                            value={value}
                            onApply={applyFilter}
                            onClear={clearFilter}
                            onCancel={close}
                          />
                        )}
                      </FilterButton>
                    </>
                  )}
                </Filters>
              </Stack>
            </Stack>
          </TableToolbar>

          <Table
            data={deposits?.items ?? []}
            isLoading={isLoading}
            columns={[
              {
                id: 'name',
                header: t('employees.company'),
                cell: ({ row: { original: balance } }) => {
                  return balance.company.name;
                },
              },
              {
                id: 'remainingDeposit',
                header: t('clients.deposits.remaining.label'),
                cell: ({ row: { original: balance } }) => {
                  return (
                    <Typography
                      color={
                        balance.remainingDeposit < 0 ? 'error' : 'textPrimary'
                      }
                    >
                      {formatCurrency(
                        i18n.language,
                        balance.remainingDeposit,
                        balance.defaultCurrency,
                      )}
                    </Typography>
                  );
                },
                meta: { align: 'right' },
              },
              {
                id: 'credit',
                header: t('common.credit.label'),
                cell: ({ row: { original: balance } }) => {
                  return formatCurrency(
                    i18n.language,
                    balance.credit,
                    balance.defaultCurrency,
                  );
                },
                meta: { align: 'right' },
              },
              {
                id: 'lowDepositAlert',
                header: t('clients.deposits.low_deposit_alert.label'),
                cell: ({ row: { original: balance } }) => {
                  return balance.lowDepositAlert < 0
                    ? t('status.disabled')
                    : formatCurrency(
                        i18n.language,
                        balance.lowDepositAlert,
                        balance.defaultCurrency,
                      );
                },
                meta: { align: 'right' },
              },
              {
                id: 'actions',
                cell: ({ row: { original: balance } }) => {
                  return (
                    <AdminClientDepositsActions
                      clientId={balance.clientId}
                      companyId={balance.company.id}
                      credit={balance.credit}
                    />
                  );
                },
              },
            ]}
            pagination={
              deposits && {
                count: deposits.total,
                page: filters.page,
                pageSize: filters.pageSize,
                pageSizeOptions: APP.pagination.defaults.pageSize,
                onChange: (page, pageSize) => {
                  navigate({ params: { page, pageSize } });
                },
              }
            }
          />
        </TableWrapper>
      </PageContentLayout>
    </Stack>
  );
};
